import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import Modal from 'react-bootstrap/Modal';
import "../styles/UserManagement.css";
import { useEffect, useState } from "react";
import loginService from "../services/loginService";
import Alert from "react-bootstrap/Alert";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import {PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from "../constants"
import DeleteUserModal from "./modals/DeleteUserModal";

const UserManagement = ({ currentUser, setCurrentUser }) => {
    const [password, setPassword] = useState(null);
    // const [email, setEmail] = useState(null);
    const [username, setUsername] = useState(null);
    const [role, setRole] = useState("admin"); // default role for new users
    const [successMessage, setSuccessMessage] = useState(null);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState(null);
    const [selectedEditedUsername, setSelectedEditedUsername] = useState(null);
    const [editedUsers, setEditedUsers] = useState({});
    const [editing, setEditing] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const { t } = useTranslation();
    const isSuperuser = currentUser.role === "superuser";

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        if (!password || !username || !role) {
            return;
        }

        const usernameValidChars = /^[a-zA-Z0-9äöåÄÖÅ\s]+$/;
        if (!usernameValidChars.test(username)) {
            setError(t("username_allowed_characters_error"));
            return;
        }

        loginService.postRegistration(username, null, password, role)
            .then((res) => {
                if (res.data.alreadyRegistered) {
                    setError(t("already_registered"));
                } else if (res.status === 201) {
                    alert(t("registration_successful"));
                    fetchUsers();
                } else {
                    setError(t("registration_failed"));
                }
            }).catch(err => console.error(err));
    };

    const handleEditClick = (name) => {
        if (name === selectedEditedUsername) {
            setSelectedEditedUsername(null);
            setEditing(false);
        } else {
            setSelectedEditedUsername(name);
            setEditing(true);
            setEditedUsers(prevState => ({
                ...prevState,
                [name]: { name } // always include username
            }));
        }
    };

    const handleInputChange = (e, field, name) => {
        if (field === "name") return; // username not editable

        setEditedUsers(prevState => {
            const originalValue = users.find(u => u.name === name)[field] || "";
            const newValue = e.target.value;

            const updatedFields = { ...prevState[name], name: name };
            if (field === "password") {
                if (newValue !== "") {
                    updatedFields[field] = newValue;
                } else {
                    delete updatedFields[field];
                }
            } else {
                if (newValue !== originalValue) {
                    updatedFields[field] = newValue;
                } else {
                    delete updatedFields[field];
                }
            }

            return { ...prevState, [name]: updatedFields };
        });
    };

    const handleDeleteClick = (username) => {
        //TODO: open modal for delete user confirmation
        setDeleteModalOpen(true);
    }

    const handleSave = () => {
        console.log("Saving edited users:", editedUsers);

        let hasError = false;

        Object.entries(editedUsers).forEach(([originalName, updatedFields]) => {
            const { password, passwordConfirm } = updatedFields

            if (Object.keys(updatedFields).length === 1 && updatedFields.name) return;

            if ((passwordConfirm || password) && (!password || !passwordConfirm || password.length === 0 || passwordConfirm.length === 0)) {
                delete updatedFields.password;
                delete updatedFields.passwordConfirm;
            }

            if ((updatedFields.password || updatedFields.passwordConfirm) && updatedFields.password !== updatedFields.passwordConfirm) {
                setError(t("user_management_password_mismatch_error"));
                hasError = true;
                return;
            }

            if (passwordConfirm) delete updatedFields.passwordConfirm;

            loginService.updateUser(originalName, updatedFields)
                .then((res) => {
                    if (res.status === 200) {
                        console.log(`User ${originalName} updated successfully`);
                        if (originalName === currentUser.name) {
                            setCurrentUser(prev => ({ ...prev, ...updatedFields }));
                        }
                        fetchUsers();
                        alert(t("user_management_user_update_success", {user: originalName}))
                    } else {
                        console.error(`Failed to update ${originalName}:`, res.data);
                    }
                })
                .catch(err => console.error(`Error updating ${originalName}:`, err));
        });

        if (!hasError) {
            setSelectedEditedUsername(null);
            setEditing(false);
            setError(null);
            setEditedUsers(null)
        }
    };

    const onUserDeleted = (errorMessage, successMessage) => {
        setError(errorMessage)
        setSuccessMessage(successMessage)
        fetchUsers();
    }

    const fetchUsers = () => {
        loginService.getUsers().then(res => {
            if (res.status !== 200) {
                setError("Fetching users failed.");
            } else {
                setUsers(res.data);
            }
        }).catch(err => console.error(err));
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <Container className="BREIKKI-user-management-main-container">
            <DeleteUserModal show={deleteModalOpen} setShow={setDeleteModalOpen} username={selectedEditedUsername} onDeleted={onUserDeleted} />
            {isSuperuser && (
                <Row>
                    <Container className="BREIKKI-user-management-registration-container">
                        <h1>{t("user_management_title")}</h1>
                        <p className="BREIKKI-user-management-registration-instructions">
                            {t("user_management_registration_instructions")}
                        </p>
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3" controlId="formUsername">
                                <Form.Label>{t("username")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t("enter_username_placeholder")}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                            </Form.Group>
                            {/* <Form.Group className="mb-3" controlId="formEmail">
                                <Form.Label>{t("email")}</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder={t("enter_email_placeholder")}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </Form.Group> */}
                            <Form.Group className="mb-1" controlId="formPassword">
                                <Form.Label>{t("password")}</Form.Label>
                                <Form.Control
                                    type="password"
                                    minLength={PASSWORD_MIN_LENGTH}
                                    maxLength={PASSWORD_MAX_LENGTH}
                                    placeholder={t("enter_password_placeholder")}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <Form.Text className="text-muted">
                                    {t("user_management_password_length_info", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formRole">
                                <Form.Label>{t("role")}</Form.Label>
                                <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
                                    <option value="admin">{t("admin")}</option>
                                    {/* Bug: setting superuser role when inviting new user doesn't work */}
                                    <option disabled value="superuser">{t("superuser")}</option>
                                </Form.Select>
                            </Form.Group>
                            <Button variant="success" type="submit">
                                {t("user_management_register_user")}
                            </Button>
                        </Form>
                    </Container>
                    {/* Delete modal */}
                </Row>

            )}

            <Row>
                {error && <Alert variant="danger" dismissible>{error}</Alert>}
                {successMessage && <Alert variant="success" dismissible>{successMessage}</Alert>}
                <h2>{t("user_management_user_table_title")}</h2>
                <Table striped bordered hover className="BREIKKI-user-management-table-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{t("username")}</th>
                            <th>{t("role")}</th>
                            <th>{t("password")}</th>
                            <th>{" "}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users && users.length > 0 ? (
                            users.map((user, index) => (
                                <tr
                                    key={user.name}
                                    className={editing && selectedEditedUsername !== user.name ? "disabled" : ""}
                                >

                                    <td>{index + 1}</td>
                                    <td>{user.name}</td>
                                    <td>
                                        {selectedEditedUsername === user.name ? (
                                            <Form.Select
                                                value={editedUsers[user.name]?.role || user.role}
                                                onChange={(e) => handleInputChange(e, "role", user.name)}
                                                disabled={!isSuperuser}
                                            >
                                                <option value="admin">{t("admin")}</option>
                                                <option value="superuser">{t("superuser")}</option>
                                            </Form.Select>
                                        ) : (
                                            user.role
                                        )}
                                    </td>
                                    <td>
                                        {selectedEditedUsername === user.name ? (
                                            <>
                                                <Form.Control
                                                    type="password"
                                                    placeholder={t("user_management_new_password_placeholder")}
                                                    value={editedUsers[user.name]?.password || ""}
                                                    onChange={(e) => {
                                                        handleInputChange(e, "password", user.name)
                                                    }}
                                                    minLength={PASSWORD_MIN_LENGTH}
                                                    maxLength={PASSWORD_MAX_LENGTH}
                                                    disabled={!isSuperuser && user.name !== currentUser.name}
                                                    className="mb-1"
                                                />
                                                <Form.Control
                                                    type="password"
                                                    placeholder={t("user_management_new_password_again_placeholder")}
                                                    value={editedUsers[user.name]?.passwordConfirm || ""}
                                                    minLength={PASSWORD_MIN_LENGTH}
                                                    maxLength={PASSWORD_MAX_LENGTH}
                                                    onChange={(e) => {
                                                        setEditedUsers(prev => ({
                                                            ...prev,
                                                            [user.name]: {
                                                                ...prev[user.name],
                                                                passwordConfirm: e.target.value,
                                                                name: user.name
                                                            }
                                                        }));
                                                    }}
                                                    disabled={!isSuperuser && user.name !== currentUser.name}
                                                />
                                                <Form.Text className="text-muted">
                                                    {t("user_management_password_length_info", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
                                                </Form.Text>
                                            </>
                                        ) : ("••••••••")
                                    }
                                    </td>
                                    <td>
                                        {(user.name === currentUser.name || isSuperuser) && (
                                            <span>
                                                {selectedEditedUsername === user.name ? (
                                                    <div className="BREIKKI-user-management-controls">
                                                        <Icon X onClick={() => handleEditClick(user.name)} className="BREIKKI-user-management-edit-user-btn" />
                                                        {currentUser.name !== user.name && <Icon del className="BREIKKI-user-management-edit-user-btn" onClick={() => handleDeleteClick(user.name)} />}
                                                    </div>
                                                ) : (
                                                    <Icon edit onClick={() => handleEditClick(user.name)} className="BREIKKI-user-management-edit-user-btn" />
                                                )}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center">
                                    {/* {t("user_management_user_list_not_found")} */}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Row>
            {editing && (
                <Button variant="primary" onClick={handleSave} className="mt-3">
                    {t("user_management_save_changes")}
                </Button>
            )}
        </Container>
    );
};

export default UserManagement;
